/* eslint-disable no-underscore-dangle */
import Vue from 'vue';

const DEFAULT_HEALTH_PATH = '/health';
const DEFAULT_TIMEOUT = 5000;

const SERVICE_STATUS = {
  UP: 'UP',
  DOWN: 'DOWN',
  TIMEOUT: 'TIMEOUT',
  UNREACHABLE: 'UNREACHABLE',
};

const OVERALL_STATUS = {
  OK: 'OK',
  PARTIAL: 'PARTIAL',
  ERROR: 'ERROR',
  UNAUTHORIZED: 'UNAUTHORIZED',
};

const createDefaultServices = () => ({
  'application-pages': { basePath: '/application-pages', healthPath: DEFAULT_HEALTH_PATH },
  'auth-service': { basePath: '/auth-service', healthPath: DEFAULT_HEALTH_PATH },
  'company-drive': { basePath: '/company-drive', healthPath: DEFAULT_HEALTH_PATH },
  'company-settings': { basePath: '/company-settings', healthPath: DEFAULT_HEALTH_PATH },
  'dynamic-apps': { basePath: '/dynamic-apps', healthPath: '/healthz' },
  maintenance: { basePath: '/maintenance', healthPath: DEFAULT_HEALTH_PATH },
  nocode: { basePath: '/nocode', healthPath: '/healthz' },
  'nocode-dedicated-job': { basePath: '/nocode-dedicated-job', healthPath: DEFAULT_HEALTH_PATH,
  },
});

const getTokenFromLocalStorage = () => {
  const rawToken = localStorage.getItem('Token');
  if (!rawToken) {
    return null;
  }

  try {
    const parsedToken = JSON.parse(rawToken);
    if (parsedToken && parsedToken.value) {
      return parsedToken.value;
    }
  } catch (error) {
    return rawToken;
  }

  return rawToken;
};

const invalidateToken = () => {
  try {
    if (Vue.ls && typeof Vue.ls.remove === 'function') {
      Vue.ls.remove('Token');
    }
  } catch (error) {
    // no-op
  }

  localStorage.removeItem('Token');
};

const resolveToken = (providedToken) => {
  if (typeof providedToken === 'string' && providedToken.trim()) {
    return {
      value: providedToken.trim(),
      source: 'argument',
    };
  }

  const vueToken = Vue.ls && typeof Vue.ls.get === 'function' ? Vue.ls.get('Token') : null;
  if (typeof vueToken === 'string' && vueToken.trim()) {
    return {
      value: vueToken.trim(),
      source: 'localStorage',
    };
  }

  const nativeToken = getTokenFromLocalStorage();
  if (typeof nativeToken === 'string' && nativeToken.trim()) {
    return {
      value: nativeToken.trim(),
      source: 'localStorage',
    };
  }

  invalidateToken();

  return {
    value: null,
    source: 'none',
  };
};

const buildUrl = (baseURL, healthPath) => {
  const cleanBaseURL = baseURL.replace(/\/$/, '');
  const cleanHealthPath = healthPath.startsWith('/') ? healthPath : `/${healthPath}`;
  return `${cleanBaseURL}${cleanHealthPath}`;
};

const normalizeBaseURL = (baseURL) => {
  if (!baseURL || typeof baseURL !== 'string') {
    return '';
  }

  const trimmedURL = baseURL.trim();

  const withoutWrappingQuotes = trimmedURL
    .replace(/^"+/, '')
    .replace(/"+$/, '')
    .replace(/^'+/, '')
    .replace(/'+$/, '');

  return withoutWrappingQuotes;
};

const isObject = value => value && typeof value === 'object' && !Array.isArray(value);

const isSubsetMatch = (expected, payload) => {
  if (!isObject(expected) || !isObject(payload)) {
    return expected === payload;
  }

  return Object.keys(expected).every((key) => {
    const expectedValue = expected[key];
    const payloadValue = payload[key];

    if (isObject(expectedValue)) {
      return isSubsetMatch(expectedValue, payloadValue);
    }

    return expectedValue === payloadValue;
  });
};

const evaluateExpected = (expected, status, payload) => {
  if (expected === null || typeof expected === 'undefined') {
    return status >= 200 && status < 300;
  }

  if (typeof expected === 'number') {
    return status === expected;
  }

  if (typeof expected === 'string') {
    return payload === expected;
  }

  if (isObject(expected)) {
    return isSubsetMatch(expected, payload);
  }

  return false;
};

const normalizeServices = (runtimeConfig) => {
  const services = createDefaultServices();
  const runtimeServices = runtimeConfig.services || {};

  Object.keys(runtimeServices).forEach((serviceKey) => {
    const override = runtimeServices[serviceKey];

    if (typeof override === 'string') {
      services[serviceKey] = {
        ...(services[serviceKey] || {}),
        healthPath: override,
      };
      return;
    }

    if (isObject(override)) {
      services[serviceKey] = {
        ...(services[serviceKey] || {}),
        ...override,
      };
    }
  });

  return services;
};

const checkSingleTarget = async ({
  service,
  config,
  token,
  internalKey,
  timeout,
  defaultHealthPath,
}) => {
  const baseURL = normalizeBaseURL(process.env.GATE_URL);
  const basePath = config.basePath || '';
  const healthPath = config.healthPath || defaultHealthPath;
  const endpointPath = internalKey ? '/integration-check' : healthPath;

  if (!baseURL) {
    return {
      service,
      name: config.name || service,
      status: SERVICE_STATUS.DOWN,
      message: 'Missing base URL',
      timestamp: new Date().toISOString(),
      baseURL: null,
      url: null,
      httpStatus: null,
      pass: false,
      expected: config.expected || null,
      response: null,
      durationMs: 0,
      error: 'MISSING_BASE_URL',
    };
  }

  const url = internalKey
    ? buildUrl(baseURL, endpointPath)
    : buildUrl(buildUrl(baseURL, basePath), endpointPath);
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const headers = {
      ...(config.headers || {}),
    };

    if (config.authRequired === true) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (internalKey) {
      headers['X-Internal-Key'] = internalKey;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    const responseBody = await response.text();
    let parsedBody = responseBody;
    try {
      parsedBody = responseBody ? JSON.parse(responseBody) : null;
    } catch (error) {
      parsedBody = responseBody;
    }

    const passByExpected = evaluateExpected(config.expected, response.status, parsedBody);
    const isHttp200 = Number(response.status) === 200;
    const isPayloadStatusOk = isObject(parsedBody) && String(parsedBody.status).toLowerCase() === 'ok';
    const pass = isHttp200 || isPayloadStatusOk || passByExpected;

    return {
      service,
      name: config.name || service,
      status: pass ? SERVICE_STATUS.UP : SERVICE_STATUS.DOWN,
      message: pass ? '-' : `HTTP ${response.status}`,
      timestamp: new Date().toISOString(),
      url,
      httpStatus: response.status,
      error: null,
    };
  } catch (error) {
    clearTimeout(timeoutId);

    const isTimeout = error instanceof DOMException && error.name === 'AbortError';
    const message = isTimeout
      ? 'Request timed out'
      : error.message;

    return {
      service,
      name: config.name || service,
      status: isTimeout ? SERVICE_STATUS.TIMEOUT : SERVICE_STATUS.UNREACHABLE,
      message,
      timestamp: new Date().toISOString(),
      url,
      httpStatus: null,
      error: message,
    };
  }
};

const computeOverallStatus = (checks) => {
  const allUp = checks.every(check => check.status === SERVICE_STATUS.UP);
  if (allUp) {
    return OVERALL_STATUS.OK;
  }

  const hasUp = checks.some(check => check.status === SERVICE_STATUS.UP);
  if (hasUp) {
    return OVERALL_STATUS.PARTIAL;
  }

  return OVERALL_STATUS.ERROR;
};

const printResults = (result) => {
  const tableData = result.services.map(service => ({
    Target: service.name,
    Status: service.status,
    Message: service.message,
    URL: service.url,
    Timestamp: service.timestamp,
  }));

  /* eslint-disable no-console */
  console.log('\n%c Integration Check Results ', 'background: #222; color: #0f0; font-size: 14px; padding: 4px 8px;');
  console.log(
    `Overall Status: %c${result.status}`,
    `color: ${result.status === OVERALL_STATUS.OK ? '#0f0' : '#f00'}; font-weight: bold;`,
  );
  console.table(tableData);
  console.log('Full result:', JSON.stringify(result, null, 2));
  /* eslint-enable no-console */
};

const executeIntegrationCheck = async (providedToken) => {
  const runtimeConfig = window._integration_check_config || {};
  const tokenMeta = resolveToken(providedToken);
  const internalKey = typeof providedToken === 'string' && providedToken.trim()
    ? providedToken.trim()
    : null;

  const services = normalizeServices(runtimeConfig);
  const enabledServices = Object.keys(services)
    .filter(service => services[service] && services[service].enabled !== false);

  const hasAuthTarget = enabledServices.some(service => services[service].authRequired === true);

  if (!tokenMeta.value && hasAuthTarget && !internalKey) {
    const unauthorizedResult = {
      status: OVERALL_STATUS.UNAUTHORIZED,
      ok: false,
      reason: 'INVALID_TOKEN',
      tokenSource: tokenMeta.source,
      summary: {
        total: 0,
        up: 0,
        down: 0,
        timeout: 0,
        unreachable: 0,
      },
      services: [],
      checks: [],
    };
    printResults(unauthorizedResult);
    return unauthorizedResult;
  }

  const timeout = Number(runtimeConfig.timeout) > 0
    ? Number(runtimeConfig.timeout)
    : DEFAULT_TIMEOUT;
  const defaultHealthPath = runtimeConfig.defaultHealthPath || DEFAULT_HEALTH_PATH;

  const checks = await Promise.all(
    enabledServices
      .map(service => checkSingleTarget({
        service,
        config: services[service],
        token: tokenMeta.value,
        internalKey,
        timeout,
        defaultHealthPath,
      })),
  );

  const up = checks.filter(item => item.status === SERVICE_STATUS.UP).length;
  const down = checks.filter(item => item.status === SERVICE_STATUS.DOWN).length;
  const timeoutCount = checks.filter(item => item.status === SERVICE_STATUS.TIMEOUT).length;
  const unreachable = checks.filter(item => item.status === SERVICE_STATUS.UNREACHABLE).length;

  const result = {
    status: computeOverallStatus(checks),
    ok: up === checks.length,
    tokenSource: tokenMeta.source,
    summary: {
      total: checks.length,
      up,
      down,
      timeout: timeoutCount,
      unreachable,
    },
    services: checks,
    checks,
  };

  printResults(result);
  return result;
};

const registerIntegrationCheck = () => {
  window._integration_check_config = {
    defaultHealthPath: DEFAULT_HEALTH_PATH,
    timeout: DEFAULT_TIMEOUT,
    services: createDefaultServices(),
    ...(window._integration_check_config || {}),
  };

  window._integration_check = async token => executeIntegrationCheck(token);
};

export {
  SERVICE_STATUS,
  OVERALL_STATUS,
  createDefaultServices,
  executeIntegrationCheck,
  registerIntegrationCheck,
};



// WEBPACK FOOTER //
// ./src/services/jApiIntegrationCheck.js